import { GroupedContracts } from '@/features/contracts/contractSlice';
import { Customer } from '@/types/Customer';
import { Contract } from '@/types/Facility';
import { InfraService } from '@/types/InfraService';
import { Property } from '@/types/State';
import dayjs from 'dayjs';
import { get, size } from 'lodash';
import {
  allowedFuseSizes,
  lspProductNumbersWithTariff,
  productNumbersWithTariff,
} from './facilityUtils';

export const nackaUser = (customer: Customer | null): boolean => {
  if (!customer) return false;
  const customerAccount = getCustomerAccount(customer);
  return get(customerAccount, 'sourceSystem.id') === 1;
};

export const getCustomerAccount = (
  customer: Customer | null,
  index: number = 0,
): any => {
  return get(customer, ['customerAccounts', index]);
};

export const userIsCustomer = (customer: Customer | null) =>
  customer
    ? customer.status
      ? customer.status.id === 1
      : get(getCustomerAccount(customer), 'status.id') === 1
    : false;

export const userIsNonCustomer = (customer: Customer | null) =>
  customer
    ? customer.status
      ? customer.status.id === 0
      : get(getCustomerAccount(customer), 'status.id') === 0
    : false;

export const userHasFlexAccount = (customer: Customer | null) =>
  !!get(customer, 'paymentInfo.flexAccount');

export const userHasDirectDebit = (customer: Customer | null): boolean =>
  !!get(customer, 'paymentInfo.directDebit');

export const userIsIndividual = (customer: Customer | null): boolean =>
  get(customer, 'legalPart.id') === 1;

export const userIsActive = (customer: Customer | null): boolean =>
  get(customer, 'status.id') === 1 ||
  get(customer, 'customerAccounts[0].status.id') === 1;

export const userIsInactive = (customer: Customer | null): boolean =>
  get(customer, 'status.id') === 2 ||
  get(customer, 'customerAccounts[0].status.id') === 2;

export const userIsUpcoming = (customer: Customer | null): boolean =>
  get(customer, 'status.id') === 3 ||
  get(customer, 'customerAccounts[0].status.id') === 3;

export const userHasFlowCharge = (customer: Customer | null): boolean =>
  userHasFJVEffekttariff(customer) ||
  !!customer?.facilities?.some((facility: any) =>
    facility.contracts?.find((contract: Contract) =>
      ['INDUSTRI H', 'INDUSTRI V', 'INDUSTRI V GULD 10', 'INDUSTRI S'].includes(
        contract.productNumber,
      ),
    ),
  );

export const customerHasELEXT = (customer: Customer | null): boolean =>
  !!customer?.facilities?.some((facility: any) =>
    facility.contracts?.some(
      (contract: Contract) => contract.utilityType.name === 'ELEXT',
    ),
  );

export const customerHasEL = (customer: Customer | null): boolean =>
  !!customer?.facilities?.some((facility: any) =>
    facility.contracts?.some(
      (contract: Contract) => contract.utilityType.name === 'EL',
    ),
  );

export const customerHasFJV = (customer: Customer | null): boolean =>
  !!customer?.facilities?.some((facility: any) =>
    facility.contracts?.some(
      (contract: Contract) => contract.utilityType.name === 'FJV',
    ),
  );

export const customerHasVA = (customer: Customer | null): boolean =>
  !!customer?.facilities?.some((facility: any) =>
    facility.contracts?.some(
      (contract: Contract) => contract.utilityType.name === 'VA',
    ),
  );

export type FJVServiceLevel = 'Guld' | 'Silver' | 'Brons';

const FJV_SERVICE_LEVELS: FJVServiceLevel[] = ['Guld', 'Silver', 'Brons'];

export const getFJVServiceLevel = (
  customer: Customer | null,
): FJVServiceLevel | null => {
  const srvContracts = customer?.facilities?.flatMap(
    (facility: Property) =>
      facility.contracts?.filter(
        (contract: Contract) => contract.utilityType.name === 'SRV',
      ) ?? [],
  );

  return (
    FJV_SERVICE_LEVELS.find((level) =>
      srvContracts?.some(
        (contract: Contract) =>
          new RegExp(`^FV${level}\\d+$`, 'i').test(contract.productNumber), // Matches product numbers like FVGULD10, FVSILVER5, etc.
      ),
    ) ?? null
  );
};

export const customerHasNetworkBasedContract = (
  customer: Customer | null,
): boolean => {
  return (
    customerHasEL(customer) ||
    customerHasFJV(customer) ||
    customerHasVA(customer)
  );
};

export const userHasFJVEffekttariff = (customer: Customer | null): boolean =>
  !!customer?.facilities?.some((facility: any) =>
    facility.meteringPoints?.find(
      (meteringPoint: any) => meteringPoint.attributes?.basePower > 0,
    ),
  );

export const getOldEffekttariffMeteringPointIdsFromFacilities = (
  facilities: any,
): any[] => {
  if (!facilities) return [];
  const meteringPoints = facilities.flatMap(
    (facility: any) =>
      facility?.contracts
        ?.filter(
          (contract: any) =>
            contract.productNumber.toLowerCase() === 'el säk eff',
        )
        .map((contract: any) => contract.attributes.meteringPointId) || [],
  );

  return [...new Set(meteringPoints)];
};
export const getEffekttariffMeteringPointIdsFromFacilities = (
  facilities: any,
): any[] => {
  if (!facilities) return [];
  const meteringPoints = facilities.flatMap(
    (facility: any) =>
      facility?.contracts
        ?.filter(
          (contract: Contract) =>
            (productNumbersWithTariff.includes(
              contract.productNumber.toLowerCase(),
            ) &&
              allowedFuseSizes.includes(
                contract.attributes?.fuseSize.toLowerCase(),
              )) ||
            lspProductNumbersWithTariff.includes(
              contract.productNumber.toLowerCase(),
            ),
        )
        .map((contract: any) => contract.attributes.meteringPointId) || [],
  );

  return [...new Set(meteringPoints)];
};
export const userHasPeakTariffSingle = (customer: Customer | null): boolean =>
  getEffekttariffMeteringPointIdsFromFacilities(customer?.facilities).length >
  0;

export const userHasPeakTariffThree = (customer: Customer | null): boolean =>
  getOldEffekttariffMeteringPointIdsFromFacilities(customer?.facilities)
    .length > 0;

export const customerHasElectricity = (customer: Customer | null): boolean =>
  !!customer?.facilities?.some((facility: any) =>
    facility.infraServices?.some(
      (infraService: InfraService) =>
        infraService.name === 'EL' || infraService.name === 'ELEXT',
    ),
  );
export const hasProduction = (
  infraService: InfraService | undefined,
): boolean => {
  return (
    infraService?.meteringPointTypes?.find(
      (meteringPointType) => meteringPointType.name === 'PRODUCTION',
    ) !== undefined
  );
};

export const meteringPointIdIsProduction = (
  meteringPointId: string | undefined,
): boolean => {
  if (!meteringPointId) return false;
  if (meteringPointId.startsWith('73599913791')) return true;
  return false;
};

export const hasUMUtility = (selectedProperty: Property | null): boolean => {
  return !!selectedProperty?.utilities?.find(
    (utility) => utility?.name?.indexOf('UM') > -1,
  );
};

export const customerIsMixed = (customer: Customer | null) =>
  get(customer, 'customerType.id') === 100;

export const customerIsCompany = (customer: Customer | null) =>
  customerIsMixed(customer)
    ? customer?.customerAccounts?.find(
        (account) => account.customerAccountId === customer.customerAccountId,
      )?.legalPart.id === 2
    : get(customer, 'customerType.id') === 2;

export const userHasMultipleCustomerNumbers = (customer: Customer) =>
  size(customer.customerAccounts.filter((account) => account.status.id === 1)) >
  1;

export const customerHasAnyContracts = (
  groupedContracts: GroupedContracts,
): boolean => {
  if (!groupedContracts) return false;
  return Object.values(groupedContracts).some(
    (contracts) => contracts.length > 0,
  );
};

export const customerHasOnlyElhandel = (
  groupedContracts: GroupedContracts,
): boolean => {
  if (!groupedContracts) return false;
  const allContracts: Contract[] = Object.values(groupedContracts).flat();
  return (
    allContracts.length > 0 &&
    allContracts.every((contract) => contract.utilityType.name === 'ELEXT')
  );
};

export const customerHasRecentlyEndedElhandelContracts = (
  groupedContracts: GroupedContracts,
): boolean => {
  if (!groupedContracts) return false;
  const cutoff = dayjs().subtract(12, 'month');
  return (groupedContracts['ELEXT'] ?? []).some(
    (contract) =>
      contract.endDate &&
      dayjs(contract.endDate).isBefore(dayjs()) &&
      dayjs(contract.endDate).isAfter(cutoff),
  );
};

export const customerIsRecentElhandelOnly = (
  customer: Customer | null,
  groupedContracts: GroupedContracts,
): boolean =>
  userIsInactive(customer) &&
  customerHasOnlyElhandel(groupedContracts) &&
  customerHasRecentlyEndedElhandelContracts(groupedContracts);

export const customerIsExpiredElhandelOnly = (
  customer: Customer | null,
  groupedContracts: GroupedContracts | null,
): boolean =>
  userIsInactive(customer) &&
  groupedContracts !== null &&
  (!customerHasAnyContracts(groupedContracts) ||
    (customerHasOnlyElhandel(groupedContracts) &&
      !customerHasRecentlyEndedElhandelContracts(groupedContracts)));

export const customerCanAccessInvoicesAndReports = (
  customer: Customer | null,
  groupedContracts: GroupedContracts | null,
): boolean =>
  userIsCustomer(customer) ||
  (userIsInactive(customer) &&
    groupedContracts !== null &&
    !customerIsExpiredElhandelOnly(customer, groupedContracts));
